import { Instagram, Mail, MapPin, Phone } from "lucide-react";
import { useLang } from "@/App";
import { copy, CONTACT } from "@/i18n";
import { LogoMark } from "@/components/Logo";

const Footer = () => {
    const { lang } = useLang();
    const c = copy[lang].footer;

    return (
        <footer data-testid="site-footer" className="border-t border-white/10 bg-noir">
            <div className="max-w-7xl mx-auto px-4 sm:px-8 py-16 grid gap-12 sm:grid-cols-2 lg:grid-cols-4">
                <div>
                    <div className="flex items-center gap-3">
                        <LogoMark className="h-10" />
                    </div>
                    <p className="mt-5 text-sm text-neutral-400 leading-relaxed">{c.made}</p>
                </div>

                <div>
                    <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-crimson">{c.contacts}</h4>
                    <ul className="mt-5 space-y-3 text-sm text-neutral-300">
                        <li>
                            <a
                                data-testid="footer-address-link"
                                href={CONTACT.maps}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-start gap-2.5 hover:text-crimson-bright transition-colors"
                            >
                                <MapPin size={15} className="mt-0.5 shrink-0 text-crimson" />
                                Via Pasquale Lucchini 12
                                <br />
                                6900 Lugano
                            </a>
                        </li>
                        <li>
                            <a
                                data-testid="footer-phone-link"
                                href={CONTACT.phoneHref}
                                className="inline-flex items-center gap-2.5 hover:text-crimson-bright transition-colors"
                            >
                                <Phone size={15} className="text-crimson" />
                                {CONTACT.phoneDisplay}
                            </a>
                        </li>
                        <li>
                            <a
                                data-testid="footer-email-link"
                                href={`mailto:${CONTACT.email}`}
                                className="inline-flex items-center gap-2.5 hover:text-crimson-bright transition-colors"
                            >
                                <Mail size={15} className="text-crimson" />
                                {CONTACT.email}
                            </a>
                        </li>
                    </ul>
                </div>

                <div>
                    <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-crimson">{c.hours}</h4>
                    <ul className="mt-5 space-y-2.5 text-sm">
                        {c.days.map(([d, h], i) => (
                            <li key={i} data-testid={`footer-hours-${i}`} className="flex justify-between gap-6">
                                <span className="text-neutral-400">{d}</span>
                                <span className={["Chiuso", "Closed", "Geschlossen", "Fermé"].includes(h) ? "text-crimson-bright" : "text-neutral-200"}>
                                    {h}
                                </span>
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <h4 className="text-xs font-semibold tracking-[0.2em] uppercase text-crimson">{c.follow}</h4>
                    <div className="mt-5 flex gap-3">
                        <a
                            data-testid="footer-instagram-link"
                            href={CONTACT.instagram}
                            target="_blank"
                            rel="noopener noreferrer"
                            aria-label="Instagram"
                            className="w-11 h-11 rounded-full border border-white/15 flex items-center justify-center text-neutral-300 hover:text-white hover:bg-crimson hover:border-crimson transition-colors"
                        >
                            <Instagram size={18} />
                        </a>
                    </div>
                    <p className="mt-4 text-xs font-mono text-neutral-500">@themarket_lugano</p>
                </div>
            </div>

            <div className="border-t border-white/10 py-6">
                <div className="max-w-7xl mx-auto px-4 sm:px-8 flex flex-col sm:flex-row justify-between gap-2 text-xs text-neutral-500">
                    <span>© {new Date().getFullYear()} The Market Lugano. {c.rights}</span>
                    <span className="font-mono tracking-widest uppercase">Lugano · Ticino · CH</span>
                </div>
            </div>
        </footer>
    );
};

export default Footer;
